<template>
  <div>
    <div style="margin-bottom: 20px;">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        :clearable="false"
        format="yyyy. MM. dd."
        value-format="yyyy-MM-dd"
        :picker-options="dateRangeOptions"
        style="margin-right: 10px;"
        @change="fetchData"
      />
      <el-select
        v-model="representativeUserId"
        v-loading="userLoading"
        filterable
        @change="fetchData"
      >
        <el-option
          v-for="user in representativeUsers"
          :key="user.id"
          :value="user.id"
          :label="user.full_name"
        />
      </el-select>
      <el-button
        style="float: right;"
        :loading="downloadLoading"
        :disabled="!dateRange || !representativeUserId"
        icon="el-icon-download"
        @click="download"
      >
        Letöltés
      </el-button>
    </div>
    <el-table
      v-if="representativeUserId && dateRange"
      v-loading="loading"
      :data="payments.data"
    >
      <el-table-column
        label="#"
        width="70"
      >
        {{ payments.id }}
      </el-table-column>
      <el-table-column
        label="Terület"
        width="140"
      >
        <div
          v-for="location in payments.locations"
          :key="location.id"
        >
          {{ location.value }}
        </div>
      </el-table-column>
      <el-table-column
        label="Képviselő"
      >
        {{ payments.user_name }}
      </el-table-column>
      <el-table-column
        prop="date"
        label="Dátum"
      >
        <template slot-scope="scope">
          {{ scope.row.date | date }}
        </template>
      </el-table-column>
      <el-table-column
        prop="payment_sum"
        label="Befizetés"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row.payment_sum | currency }}
        </template>
      </el-table-column>
      <el-table-column
        prop="sale_sum"
        label="Forgalom"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row.sale_sum | currency }}
        </template>
      </el-table-column>
      <el-table-column
        prop="rolling_payment_sum"
        label="Görgetett befizetés"
        align="right"
        width="160"
      >
        <template slot-scope="scope">
          {{ scope.row.rolling_payment_sum | currency }}
        </template>
      </el-table-column>
      <el-table-column
        prop="rolling_sale_sum"
        label="Görgetett forgalom"
        align="right"
        width="160"
      >
        <template slot-scope="scope">
          {{ scope.row.rolling_sale_sum | currency }}
        </template>
      </el-table-column>
      <el-table-column
        prop="balance"
        label="Egyenleg"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row.balance | currency }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import moment from 'moment';
import { saveAs } from 'file-saver';

export default {
  data() {
    const start = moment()
      .startOf('year')
      .format('Y-MM-DD');
    const end = moment()
      .format('Y-MM-DD');
    return {
      loading: false,
      downloadLoading: false,
      userLoading: false,
      dateRange: [start, end],
      representativeUserId: null,
      representativeUsers: [],
      dateRangeOptions: {
        firstDayOfWeek: 1,
        shortcuts: [{
          text: 'Aktuális hét',
          onClick(picker) {
            const start = moment()
              .weekday(1)
              .toDate();
            const end = moment()
              .weekday(7)
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hét',
          onClick(picker) {
            const start = moment()
              .subtract({ weeks: 1 })
              .weekday(1)
              .toDate();
            const end = moment()
              .subtract({ weeks: 1 })
              .weekday(7)
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális hónap',
          onClick(picker) {
            const start = moment()
              .startOf('month')
              .toDate();
            const end = moment()
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hónap',
          onClick(picker) {
            const start = moment()
              .subtract({ months: 1 })
              .startOf('month')
              .toDate();
            const end = moment()
              .subtract({ months: 1 })
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző 3 hónap',
          onClick(picker) {
            const start = moment()
              .subtract({ months: 3 })
              .startOf('month')
              .toDate();
            const end = moment()
              .subtract({ months: 1 })
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális év',
          onClick(picker) {
            const start = moment()
              .startOf('year')
              .toDate();
            const end = moment()
              .endOf('year')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző év',
          onClick(picker) {
            const start = moment()
              .subtract({ years: 1 })
              .startOf('year')
              .toDate();
            const end = moment()
              .subtract({ years: 1 })
              .endOf('year')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'YTD',
          onClick(picker) {
            const start = moment()
              .startOf('year')
              .toDate();
            const end = moment()
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'MTD',
          onClick(picker) {
            const start = moment()
              .startOf('month')
              .toDate();
            const end = moment()
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }]
      },
      payments: {
        locations: [],
        data: [],
      },
    };
  },
  methods: {
    async download() {
      this.downloadLoading = true;
      try {
        const excel = await axios({
          url: `api/reports/representative-payments/rolling/download`,
          method: 'POST',
          responseType: 'blob',
          data: {
            from: this.dateRange[0],
            to: this.dateRange[1],
            userId: this.representativeUserId,
          },
        });
        saveAs(excel, `payments.xlsx`);
      } catch (e) {}
      this.downloadLoading = false;
    },
    async fetchData() {
      if (!this.representativeUserId) {
        return
      }
      this.loading = true;
      try {
        this.payments = await axios.get(`api/reports/representative-payments/rolling/${this.dateRange.join(',')}/${this.representativeUserId}`);
      } catch (e) {}
      this.loading = false;
    }
  },
  async mounted() {
    this.userLoading = true;
    try {
      this.representativeUsers = await axios.get('api/users/representatives');
    } catch (e) {}
    this.userLoading = false;
  },
};
</script>
